import React, { useState, useMemo } from 'react';
import type { PiletaEquipo, Operadora, Equipo, PiletaTipo } from '../../types';

interface PiletasSectionProps {
  data: {
    operadoras: Operadora[];
    equipos: Equipo[];
    piletasEquipo: PiletaEquipo[];
    tiposPiletas: PiletaTipo[];
    [key: string]: any;
  };
  operations: {
    handlePiletaEquipoSubmit: (data: Record<string, any>) => Promise<void>;
    handlePiletaEquipoDelete: (item: PiletaEquipo) => Promise<void>;
    handlePiletaEquipoEdit: (item: PiletaEquipo, key: string, newValue: any) => Promise<void>;
  };
  userPermissions: {
    canEditReports: boolean;
    canDeleteReports: boolean;
  };
}

export const PiletasSection: React.FC<PiletasSectionProps> = ({ data, operations, userPermissions }) => {
  // Predefined tank names
  const tankNameOptions = useMemo(() => {
    const names = [
      'Preparadora',
      'Preparadora A',
      'Preparadora B'
    ];

    // Add Pileta Auxiliar #1-8
    for (let i = 1; i <= 8; i++) {
      names.push(`Pileta Auxiliar #${i}`);
    }

    // Add Pileta Contingencia #1-4
    for (let i = 1; i <= 4; i++) {
      names.push(`Pileta Contingencia #${i}`);
    }

    return names;
  }, []);

  // State for cascading selectors
  const [selectedOperadoraId, setSelectedOperadoraId] = useState<string>('');
  const [selectedEquipoId, setSelectedEquipoId] = useState<string>('');
  const [newPiletaData, setNewPiletaData] = useState({
    nombre: '',
    serial: '',
    tipoId: ''
  });

  // Helper functions to match iOS sorting logic
  const getTankTypePriority = (nombre: string): number => {
    const nombreLower = nombre.toLowerCase();
    if (nombreLower === "preparadora a") {
      return 0;
    } else if (nombreLower === "preparadora b") {
      return 1;
    } else if (nombreLower.includes("preparadora")) {
      return 2;
    } else if (nombreLower.includes("auxiliar")) {
      return 3;
    } else if (nombreLower.includes("contingencia")) {
      return 4;
    } else {
      return 5;
    }
  };

  const extractNumberFromName = (nombre: string): number => {
    const match = nombre.match(/\d+/);
    return match ? parseInt(match[0], 10) : Number.MAX_SAFE_INTEGER;
  };

  // Filter equipos by selected operadora
  const filteredEquipos = useMemo(() => {
    if (!selectedOperadoraId) return [];
    return data.equipos.filter(equipo => equipo.operadoraId === selectedOperadoraId);
  }, [data.equipos, selectedOperadoraId]);

  // Filter and sort piletas by selected equipment
  const filteredPiletas = useMemo(() => {
    if (!selectedEquipoId) return [];

    const filtered = data.piletasEquipo.filter(pileta => pileta.equipoId === selectedEquipoId);

    // Sort to match iOS app ordering: valid first, then by type and number
    return filtered.sort((lhs, rhs) => {
      // First: valid tanks before invalid ones
      if (lhs.habilitada !== rhs.habilitada) {
        return lhs.habilitada && !rhs.habilitada ? -1 : 1;
      }

      // Second: sort by tank type priority
      const lhsPrioridad = getTankTypePriority(lhs.nombre);
      const rhsPrioridad = getTankTypePriority(rhs.nombre);
      if (lhsPrioridad !== rhsPrioridad) {
        return lhsPrioridad - rhsPrioridad;
      }

      // Third: sort by extracted number from name
      const lhsNumero = extractNumberFromName(lhs.nombre);
      const rhsNumero = extractNumberFromName(rhs.nombre);
      return lhsNumero - rhsNumero;
    });
  }, [data.piletasEquipo, selectedEquipoId]);

  // Reset cascading selections
  const handleOperadoraChange = (operadoraId: string) => {
    setSelectedOperadoraId(operadoraId);
    setSelectedEquipoId('');
    setNewPiletaData({ nombre: '', serial: '', tipoId: '' });
  };

  const handleEquipoChange = (equipoId: string) => {
    setSelectedEquipoId(equipoId);
    setNewPiletaData({ nombre: '', serial: '', tipoId: '' });
  };

  // Handle pileta creation
  const handleAddPileta = async () => {
    if (!selectedOperadoraId || !selectedEquipoId || !newPiletaData.nombre.trim() || !newPiletaData.tipoId) {
      alert('❌ Todos los campos son requeridos');
      return;
    }

    try {
      await operations.handlePiletaEquipoSubmit({
        ...newPiletaData,
        equipoId: selectedEquipoId,
        habilitada: true
      });
      setNewPiletaData({ nombre: '', serial: '', tipoId: '' });
      alert('✅ Pileta agregada exitosamente');
    } catch (error) {
      console.error('Error adding pileta:', error);
      alert('❌ Error agregando pileta');
    }
  };

  // Get selected context names
  const selectedOperadora = data.operadoras.find(op => op.id === selectedOperadoraId);
  const selectedEquipo = data.equipos.find(eq => eq.id === selectedEquipoId);

  return (
    <>
      <div style={{ marginBottom: '30px', textAlign: 'center' }}>
        <h2>🛢️ Gestión de Piletas</h2>
        <p style={{ color: '#666', marginTop: '10px' }}>
          Administra las piletas por operadora y equipo. Cada pileta tiene un tipo que define su tabla de volúmenes.
        </p>
      </div>


      {/* Progressive Disclosure Selectors */}
      <div style={{
        marginBottom: '30px',
        padding: '20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '12px',
        border: '2px solid #dee2e6'
      }}>
        <h3 style={{ marginBottom: '20px', color: '#495057' }}>🔍 Selección de Contexto</h3>

        {/* Side by side selectors */}
        <div style={{ display: 'flex', gap: '20px', marginBottom: '15px', flexWrap: 'wrap' }}>
          {/* Step 1: Operadora Selector */}
          <div style={{ flex: '1', minWidth: '250px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#495057' }}>
              🏢 Seleccionar Operadora:
            </label>
            <select
              value={selectedOperadoraId}
              onChange={(e) => handleOperadoraChange(e.target.value)}
              style={{
                padding: '12px',
                borderRadius: '6px',
                border: '2px solid #dee2e6',
                fontSize: '14px',
                width: '100%',
                backgroundColor: 'white'
              }}
            >
              <option value="">-- Seleccionar Operadora --</option>
              {data.operadoras.filter(op => op.habilitada).map(operadora => (
                <option key={operadora.id} value={operadora.id}>
                  {operadora.nombre}
                </option>
              ))}
            </select>
          </div>

          {/* Step 2: Equipo Selector */}
          <div style={{ flex: '1', minWidth: '250px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#495057' }}>
              🔧 Seleccionar Equipo:
            </label>
            <select
              value={selectedEquipoId}
              onChange={(e) => handleEquipoChange(e.target.value)}
              disabled={!selectedOperadoraId}
              style={{
                padding: '12px',
                borderRadius: '6px',
                border: '2px solid #dee2e6',
                fontSize: '14px',
                width: '100%',
                backgroundColor: !selectedOperadoraId ? '#f8f9fa' : 'white',
                cursor: !selectedOperadoraId ? 'not-allowed' : 'pointer'
              }}
            >
              <option value="">-- Seleccionar Equipo --</option>
              {filteredEquipos.filter(eq => eq.habilitado).map(equipo => (
                <option key={equipo.id} value={equipo.id}>
                  {equipo.nombre}
                </option>
              ))}
            </select>
            {selectedOperadoraId && filteredEquipos.length === 0 && (
              <div style={{ marginTop: '8px', color: '#6c757d', fontSize: '14px' }}>
                No hay equipos disponibles para esta operadora
              </div>
            )}
          </div>
        </div>

        {/* Step 3: Pileta Creation Form (appears when both are selected) */}
        {selectedOperadoraId && selectedEquipoId && (
          <div style={{ backgroundColor: '#ffffff', padding: '20px', borderRadius: '8px', border: '1px solid #dee2e6' }}>
            <h4 style={{ marginBottom: '15px', color: '#495057' }}>➕ Agregar Nueva Pileta</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '15px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#495057' }}>
                  📝 Nombre:
                </label>
                <select
                  value={newPiletaData.nombre}
                  onChange={(e) => setNewPiletaData({...newPiletaData, nombre: e.target.value})}
                  style={{
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #ced4da',
                    fontSize: '14px',
                    width: '100%'
                  }}
                >
                  <option value="">-- Seleccionar Nombre --</option>
                  {tankNameOptions.map(name => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#495057' }}>
                  🔢 Serial:
                </label>
                <input
                  type="text"
                  value={newPiletaData.serial}
                  onChange={(e) => setNewPiletaData({...newPiletaData, serial: e.target.value})}
                  placeholder="Ej: TK-001, SER123, etc."
                  style={{
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #ced4da',
                    fontSize: '14px',
                    width: '100%'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#495057' }}>
                  📊 Tipo de Pileta:
                </label>
                <select
                  value={newPiletaData.tipoId}
                  onChange={(e) => setNewPiletaData({...newPiletaData, tipoId: e.target.value})}
                  style={{
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #ced4da',
                    fontSize: '14px',
                    width: '100%'
                  }}
                >
                  <option value="">-- Seleccionar Tipo --</option>
                  {data.tiposPiletas.map(tipo => (
                    <option key={tipo.id} value={tipo.id}>
                      {tipo.nombre} ({tipo.tabla})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <button
              onClick={handleAddPileta}
              disabled={!newPiletaData.nombre.trim() || !newPiletaData.serial.trim() || !newPiletaData.tipoId}
              style={{
                padding: '12px 20px',
                backgroundColor: (!newPiletaData.nombre.trim() || !newPiletaData.serial.trim() || !newPiletaData.tipoId) ? '#6c757d' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: (!newPiletaData.nombre.trim() || !newPiletaData.serial.trim() || !newPiletaData.tipoId) ? 'not-allowed' : 'pointer'
              }}
            >
              ➕ Agregar Pileta
            </button>
          </div>
        )}
      </div>

      {/* Piletas List (appears when equipment is selected) */}
      {selectedOperadoraId && selectedEquipoId && (
        <div style={{
          marginTop: '30px',
          padding: '20px',
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '2px solid #28a745'
        }}>
          <h3 style={{ color: '#155724', marginBottom: '15px' }}>
            🛢️ Piletas de {selectedEquipo?.nombre} ({selectedOperadora?.nombre})
          </h3>

          {filteredPiletas.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Nombre</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Serial</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Tipo</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Tabla</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Estado</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPiletas.map(pileta => {
                    const tipoPileta = data.tiposPiletas.find(tipo => tipo.id === pileta.tipoId);
                    return (
                      <tr key={pileta.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                        <td style={{ padding: '12px' }}>
                          <select
                            value={pileta.nombre}
                            onChange={(e) => operations.handlePiletaEquipoEdit(pileta, 'nombre', e.target.value)}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              width: '100%'
                            }}
                          >
                            {tankNameOptions.map(name => (
                              <option key={name} value={name}>
                                {name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td style={{ padding: '12px' }}>
                          <input
                            type="text"
                            value={pileta.serial}
                            onChange={(e) => operations.handlePiletaEquipoEdit(pileta, 'serial', e.target.value)}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              fontSize: '14px',
                              width: '100%'
                            }}
                          />
                        </td>
                        <td style={{ padding: '12px' }}>
                          <select
                            value={pileta.tipoId}
                            onChange={(e) => operations.handlePiletaEquipoEdit(pileta, 'tipoId', e.target.value)}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              fontSize: '14px',
                              width: '100%'
                            }}
                          >
                            {data.tiposPiletas.map(tipo => (
                              <option key={tipo.id} value={tipo.id}>
                                {tipo.nombre}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td style={{ padding: '12px', color: '#6c757d', fontSize: '14px' }}>
                          {tipoPileta?.tabla || 'Sin tabla'}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            backgroundColor: pileta.habilitada ? '#d4edda' : '#f8d7da',
                            color: pileta.habilitada ? '#155724' : '#721c24'
                          }}>
                            {pileta.habilitada ? 'Activa' : 'Inactiva'}
                          </span>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <button
                            onClick={() => operations.handlePiletaEquipoEdit(pileta, 'habilitada', !pileta.habilitada)}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: pileta.habilitada ? '#ffc107' : '#28a745',
                              color: pileta.habilitada ? '#212529' : 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              marginRight: '8px'
                            }}
                          >
                            {pileta.habilitada ? '⏸️ Desactivar' : '▶️ Activar'}
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm(`¿Eliminar pileta ${pileta.nombre}?`)) {
                                operations.handlePiletaEquipoDelete(pileta);
                              }
                            }}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '12px',
                              cursor: 'pointer'
                            }}
                          >
                            🗑️ Eliminar
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#6c757d',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '2px dashed #dee2e6'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '15px' }}>🛢️</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>No hay piletas registradas</div>
              <div style={{ fontSize: '14px' }}>
                Para este equipo en la operadora seleccionada.
                <br />Use el formulario superior para agregar la primera pileta.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Summary Section */}
      <div style={{
        marginTop: '30px',
        padding: '20px',
        backgroundColor: '#e8f5e8',
        borderRadius: '12px',
        border: '2px solid #4CAF50'
      }}>
        <h3 style={{ color: '#2E7D32', marginBottom: '15px' }}>📊 Resumen de Piletas</h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px',
          marginTop: '15px'
        }}>
          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              🛢️ Total Piletas
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#4CAF50'
            }}>
              {data.piletasEquipo.length}
            </div>
          </div>

          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              ✅ Piletas Activas
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#28a745'
            }}>
              {data.piletasEquipo.filter(pileta => pileta.habilitada).length}
            </div>
          </div>

          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              📊 Tipos Disponibles
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#007bff'
            }}>
              {data.tiposPiletas.length}
            </div>
          </div>

          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              🏢 Operadoras
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#6f42c1'
            }}>
              {data.operadoras.filter(op => op.habilitada).length}
            </div>
          </div>
        </div>

        {/* Empty State */}
        {data.piletasEquipo.length === 0 && (
          <div style={{ textAlign: 'center', color: '#666', padding: '20px', marginTop: '20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>🛢️</div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>No hay piletas registradas</div>
            <div style={{ fontSize: '14px' }}>
              Selecciona una operadora y equipo para agregar piletas.
            </div>
          </div>
        )}
      </div>
    </>
  );
};